import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.hash?.includes("session_id=")) return;
    const token = localStorage.getItem("mf_token");
    axios
      .get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => navigate("/admin", { state: { user: res.data }, replace: true }))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
      localStorage.setItem("mf_token", data.token);
      navigate("/admin", { state: { user: data.user }, replace: true });
    } catch {
      setError("Credenziali non valide.");
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6" data-testid="login-page">
      <div className="w-full max-w-md border border-ink/12 bg-white p-8 lg:p-10">
        <Link to="/" className="flex flex-col leading-none" data-testid="login-logo-link">
          <span className="font-serif text-2xl font-medium tracking-tight text-ink">
            Marketing <span className="italic text-accent">Facile</span>
          </span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-mutedink">Area riservata</span>
        </Link>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-xs uppercase tracking-[0.12em] text-mutedink">Email</Label>
            <Input
              id="login-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="rounded-none border-ink/20 px-4 py-3 text-sm focus-visible:ring-1 focus-visible:ring-accent"
              data-testid="login-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-xs uppercase tracking-[0.12em] text-mutedink">Password</Label>
            <Input
              id="login-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="rounded-none border-ink/20 px-4 py-3 text-sm focus-visible:ring-1 focus-visible:ring-accent"
              data-testid="login-password"
            />
          </div>
          {error && <p className="text-sm text-red-700" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-accent disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Accedi
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-ink/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">oppure</span>
          <span className="h-px flex-1 bg-ink/10" />
        </div>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="login-google-button"
          className="inline-flex w-full items-center justify-center gap-3 border border-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
        >
          Continua con Google
        </button>
      </div>
    </div>
  );
};

export default Login;
